/**
 * Serviço de Pedidos
 *
 * Funções para comunicação com a API de pedidos.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { api } from './api';
import { ApiResponse, ApiMeta, PaginationParams } from '@/types/common';
import {
  Order,
  OrderFilters,
  OrderStatus,
  OrdersSummary,
  CreateOrderDTO,
} from '@/types/order';

/**
 * Lista pedidos com filtros e paginação
 *
 * @param params - Parâmetros de busca
 * @returns Lista de pedidos e metadados
 */
export async function listOrders(
  params: OrderFilters & PaginationParams = {},
): Promise<{ data: Order[]; meta: ApiMeta }> {
  const queryParams = new URLSearchParams();

  // Adiciona parâmetros
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      queryParams.append(key, String(value));
    }
  });

  const response = await api.get<ApiResponse<Order[]>>(
    `/orders?${queryParams.toString()}`,
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar pedidos');
  }

  return {
    data: response.data.data,
    meta: response.data.meta!,
  };
}

/**
 * Busca um pedido por ID
 *
 * @param id - ID do pedido
 * @returns Pedido encontrado
 */
export async function getOrder(id: string): Promise<Order> {
  const response = await api.get<ApiResponse<Order>>(`/orders/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Pedido não encontrado');
  }

  return response.data.data;
}

/**
 * Cria um novo pedido
 *
 * @param data - Dados do pedido
 * @returns Pedido criado
 */
export async function createOrder(data: CreateOrderDTO): Promise<Order> {
  const response = await api.post<ApiResponse<Order>>('/orders', data);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao criar pedido');
  }

  return response.data.data;
}

/**
 * Atualiza status de um pedido
 *
 * @param id - ID do pedido
 * @param status - Novo status
 * @param reason - Motivo (para cancelamento, enviado como notes)
 * @returns Pedido atualizado
 */
export async function updateOrderStatus(
  id: string,
  status: OrderStatus,
  reason?: string,
): Promise<Order> {
  const response = await api.patch<ApiResponse<Order>>(`/orders/${id}/status`, {
    status,
    notes: reason,
  });

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao atualizar status');
  }

  return response.data.data;
}

/**
 * Cancela um pedido
 *
 * @param id - ID do pedido
 * @param reason - Motivo do cancelamento
 * @returns Pedido cancelado
 */
export async function cancelOrder(id: string, reason: string): Promise<Order> {
  return updateOrderStatus(id, 'CANCELLED', reason);
}

/**
 * Confirma um pedido
 *
 * @param id - ID do pedido
 * @param estimatedPrepTime - Tempo estimado de preparo (min)
 * @returns Pedido confirmado
 */
export async function confirmOrder(
  id: string,
  estimatedPrepTime?: number,
): Promise<Order> {
  const response = await api.patch<ApiResponse<Order>>(`/orders/${id}/confirm`, {
    estimatedPrepTime,
  });

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao confirmar pedido');
  }

  return response.data.data;
}

/**
 * Marca pedido como pronto
 *
 * @param id - ID do pedido
 * @returns Pedido atualizado
 */
export async function markAsReady(id: string): Promise<Order> {
  return updateOrderStatus(id, 'READY');
}

/**
 * Marca pedido como em entrega
 *
 * @param id - ID do pedido
 * @param deliveryPersonId - ID do entregador
 * @returns Pedido atualizado
 */
export async function markAsDelivering(
  id: string,
  deliveryPersonId?: string,
): Promise<Order> {
  const response = await api.patch<ApiResponse<Order>>(`/orders/${id}/dispatch`, {
    deliveryPersonId,
  });

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao despachar pedido');
  }

  return response.data.data;
}

/**
 * Marca pedido como entregue
 *
 * @param id - ID do pedido
 * @returns Pedido atualizado
 */
export async function markAsDelivered(id: string): Promise<Order> {
  return updateOrderStatus(id, 'DELIVERED');
}

/**
 * Marca pedido como concluído
 *
 * @param id - ID do pedido
 * @returns Pedido atualizado
 */
export async function completeOrder(id: string): Promise<Order> {
  return updateOrderStatus(id, 'COMPLETED');
}

/**
 * Obtém resumo de pedidos do dia
 *
 * @returns Resumo de pedidos
 */
export async function getOrdersSummary(): Promise<OrdersSummary> {
  const response = await api.get<ApiResponse<OrdersSummary>>('/orders/summary');

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar resumo');
  }

  return response.data.data;
}

/**
 * Imprime pedido (gera PDF)
 *
 * @param id - ID do pedido
 * @returns Blob do PDF
 */
export async function printOrder(id: string): Promise<Blob> {
  const response = await api.get(`/orders/${id}/print`, {
    responseType: 'blob',
  });

  return response.data;
}

/**
 * Confirma manualmente o pagamento PIX de um pedido
 *
 * @param id - ID do pedido
 * @returns Resultado da confirmacao
 */
export async function confirmPayment(id: string): Promise<{ paymentStatus: string; paidAt: string }> {
  const response = await api.post<ApiResponse<{ paymentStatus: string; paidAt: string }>>(`/orders/${id}/confirm-payment`);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao confirmar pagamento');
  }

  return response.data.data;
}

export default {
  listOrders,
  getOrder,
  createOrder,
  updateOrderStatus,
  cancelOrder,
  confirmOrder,
  markAsReady,
  markAsDelivering,
  markAsDelivered,
  completeOrder,
  getOrdersSummary,
  printOrder,
  confirmPayment,
};
